if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        if (this.form.querySelector("[name=id]")) {
          this.form.querySelector("[name=id]").disabled = false;
        }
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');

        if (document.querySelector("cart-drawer")) {
          this.submitButton.setAttribute("aria-haspopup", "dialog");
        }

        this.hideErrors = this.dataset.hideErrors === "true";
        this.enableSizeConfirmation =
          window.themeSettings?.enableSizeConfirmation || false;
        this.enablePreorderConfirmation =
          window.themeSettings?.enablePreorderConfirmation || false;
        this.inventorySuffix = this.dataset.inventorySuffix;
      }

      getMetafieldInventory(variantId) {
        const key = `${variantId}-${this.inventorySuffix}`;
        return window.locationInventoryData[key] || 0;
      }

      getQuantityInCart(cartData, variantId) {
        if (!cartData?.items) return 0;
        return cartData.items
          .filter(item => item.variant_id === parseInt(variantId, 10))
          .reduce((sum, item) => sum + item.quantity, 0);
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();

        // if no quantity selector (quick-add), bypass inventory checks
        const qtyInput = this.form.querySelector("[name=quantity]");
        if (!qtyInput) {
          return this.handleFormSubmission();
        }

        const variantId = this.form.querySelector("[name=id]").value;
        const key = `${variantId}-${this.inventorySuffix}`;
        if (!Object.prototype.hasOwnProperty.call(window.locationInventoryData, key)) {
          return this.handleFormSubmission();
        }

        // 1. Calculate stock vs desired quantity
        const desiredQuantity = parseInt(
          this.form.querySelector("[name=quantity]").value,
          10
        );
        const maxStock        = this.getMetafieldInventory(variantId);
        const cartData        = await this.fetchCart();
        const existingQty     = this.getQuantityInCart(cartData, variantId);
        const availableToAdd  = maxStock - existingQty;
        const quantityToAdd   = Math.min(desiredQuantity, availableToAdd);
        const partialAdd      = desiredQuantity > availableToAdd;

        // store for handleFormSubmission
        this._availableToAdd = availableToAdd;
        this._existingQty    = existingQty;
        this._partialAdd     = partialAdd;
        this._partialQty     = quantityToAdd;

        // override form quantity
        this.form.querySelector("[name=quantity]").value = quantityToAdd;

        // 2. Size / Preorder confirmation
        const sizeOption = this.form.querySelector("[name=size-option]");
        const size = sizeOption ? sizeOption.value : null;

        if (size === "2XS" && this.enableSizeConfirmation) {
          const badgeCart = await this.fetchCart();
          const has2xs    = this.checkSizeInCart(badgeCart, "2XS");
          if (!has2xs) {
            const pop = document.getElementById("confirm-size-pop-up");
            pop.style.display = "flex";
            document
              .getElementById("confirm-size-yes")
              .addEventListener(
                "click",
                () => { pop.style.display = "none"; this.handleFormSubmission(); },
                { once: true }
              );
            document
              .getElementById("confirm-size-no")
              .addEventListener(
                "click",
                () => { pop.style.display = "none"; this.resetButtonState(); },
                { once: true }
              );
          } else this.handleFormSubmission();
        } else if (this.enablePreorderConfirmation) {
          const hasPre = this.checkPreorderBadge();
          if (hasPre) this.handleFormSubmission();
          else if (
            this.form.querySelector("#preorder_consolidated")?.name ===
              "properties[Pre-order]" ||
            this.form.querySelector("#us_preorder_consolidated")?.name ===
              "properties[Pre-order]"
          ) {
            const pop = document.getElementById("confirm-preorder");
            pop.style.display = "flex";
            document
              .getElementById("confirm-preorder-yes")
              .addEventListener(
                "click",
                () => { pop.style.display = "none"; this.handleFormSubmission(); },
                { once: true }
              );
            document
              .getElementById("confirm-preorder-no")
              .addEventListener(
                "click",
                () => { pop.style.display = "none"; this.resetButtonState(); },
                { once: true }
              );
          } else this.handleFormSubmission();
        } else {
          this.handleFormSubmission();
        }
      }

      resetButtonState() {
        this.submitButton.removeAttribute("aria-disabled");
        this.submitButton.classList.remove("loading");
        this.querySelector(
          ".loading-overlay__spinner"
        ).classList.add("hidden");
      }

      async fetchCart() {
        try {
          const res = await fetch("/cart.js", {
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "XMLHttpRequest"
            }
          });
          return await res.json();
        } catch (e) {
          console.error("Failed to fetch cart data:", e);
          return null;
        }
      }

      checkSizeInCart(cartData, size) {
        return Array.isArray(cartData?.items) && cartData.items.some(item =>
          item.options_with_values?.some(o => o.name === "Size" && o.value === size)
        );
      }

      checkPreorderBadge() {
        return Array.from(
          document.querySelectorAll(".cart-item .product-option dt")
        ).some(dt => dt.textContent.trim() === "Pre-order:");
      }

      handleFormSubmission() {
        // 1) quantity error if none available
        if (this._availableToAdd <= 0) {
          const msg = this._existingQty === 1
            ? `You already have 1 item in your cart, which is the maximum available.`
            : `You already have ${this._existingQty} items in your cart, which are the maximum available.`;
          this.handleErrorMessage(msg);
          this.resetButtonState();
          return;
        }

        // 2) show loading
        this.handleErrorMessage();
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(
          ".loading-overlay__spinner"
        ).classList.remove("hidden");

        // 3) prepare request
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map(s => s.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // 4) send AJAX
        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              // server error
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButton.querySelector("span").classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response
              });
            this.error = false;
            const quickAdd = this.closest("quick-add-modal");
            if (quickAdd) {
              document.body.addEventListener(
                "modalClosed",
                () => this.cart.renderContents(response),
                { once: true }
              );
              quickAdd.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch(e => console.error(e))
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading-overlay__spinner").classList.add(
              "hidden"
            );

            if (this._partialAdd) {
              const msg = this._partialQty === 1
                ? `Only 1 item was added to your cart due to stock limits.`
                : `Only ${this._partialQty} items were added to your cart due to stock limits.`;
              this.handleErrorMessage(msg);
            }

            // normal success
            this.showAddToCartSuccessMessage();
            document.dispatchEvent(
              new CustomEvent("cart:addtocart_complete", {
                detail: { cart: this.cart }
              })
            );
            
            this._partialAdd = false;
            
            // Reset quantity to 1 after successful add to cart
            const qtyInput = this.form.querySelector("[name=quantity]");
            if (qtyInput) {
              qtyInput.value = 1;
              // Trigger validation to update button states
              qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }
      showAddToCartSuccessMessage() {
        const successMessage = this.submitButton.querySelector(
          ".add-to-cart-success-message"
        );
        const addToCartText =
          this.submitButton.querySelector(".add-to-cart-text");

        if (successMessage && addToCartText) {
          addToCartText.classList.add("hidden");
          successMessage.classList.remove("hidden");
          setTimeout(() => {
            successMessage.classList.add("hidden");
            addToCartText.classList.remove("hidden");
          }, 3000);
        }
      }
    }
  );
}